import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, fmtMoney, fmtNum } from "@/lib/api";
import { useMasters, opt, vehicleOptions, matchVehicle, partiesByRole, partiesByRoles, useLabourRate } from "@/lib/useMasters";
import { useAuth } from "@/context/AuthContext";
import { useOnline } from "@/lib/useOnline";
import { PageHeader, Card, Field, SelectInput, TextInput, NumberInput, TextArea, SectionTitle, SuggestInput } from "@/components/kit";
import TxnRecent, { outwardColumns, outwardFields } from "@/components/TxnRecent";
import { Loader2, WifiOff, ArrowUpFromLine } from "lucide-react";

const CHARGE_WHO = [
  { value: "COMPANY", label: "Company bear" },
  { value: "STOCK_OWNER", label: "Stock Owner" },
  { value: "SENDER", label: "Receiver" },
];

function resolveChargeParty(who, f, lot) {
  if (who === "STOCK_OWNER") return lot?.stock_owner_id ? +lot.stock_owner_id : null;
  if (who === "SENDER") return f.receiver_id ? +f.receiver_id : null;
  return null;
}

export default function Outward() {
  const m = useMasters();
  const online = useOnline();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const { data: lots = [] } = useQuery({
    queryKey: ["lots-active"],
    queryFn: async () => (await api.get("/reports/lots")).data,
  });
  const activeLots = lots.filter((l) => l.current_bags > 0);

  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10), time: new Date().toTimeString().slice(0, 5),
    lot_id: "", receiver_id: "", vehicle_id: "", vehicle_number: "", driver: "", destination: "", dispatch_reference: "",
    bags: "", net_weight: "", remarks: "",
    lab_contractor_id: "", lab_rate: "", bag_lab_rate: "",
    expected_dala: "", dala_from_vehicle: "", dala_recover_from: "STOCK_OWNER",
    torn_qty: "", spill_qty: "", bag_charge_to: "STOCK_OWNER",
    shrinkage_approved: false,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const receiverOpts = opt(partiesByRoles(m.parties, ["Receiver", "Buyer"]), (p) => `${p.name} (${p.code})`);
  const labourOpts = opt(partiesByRole(m.parties, "Labour Contractor"), (p) => `${p.name} (${p.code})`);
  const lot = useMemo(() => activeLots.find((l) => String(l.id) === String(f.lot_id)), [f.lot_id, activeLots]);
  const lotBagTypeId = lot?.bag_type_id;
  const bagType = useMemo(
    () => (m.bagTypes || []).find((b) => String(b.id) === String(lotBagTypeId)),
    [m.bagTypes, lotBagTypeId],
  );
  const bagRate = Number(bagType?.charge_rate || 0);
  const tornQty = Number(f.torn_qty || 0);
  const spillQty = Number(f.spill_qty || 0);
  const stockBags = Number(f.bags || 0);
  const dispatchedBags = stockBags + spillQty;
  const chargeBags = tornQty + spillQty;
  const { data: labRate } = useLabourRate(f.lab_contractor_id, lotBagTypeId);
  useEffect(() => {
    if (!f.lab_contractor_id || !lotBagTypeId) {
      setF((s) => (s.lab_rate || s.bag_lab_rate ? { ...s, lab_rate: "", bag_lab_rate: "" } : s));
      return;
    }
    if (labRate?.found) {
      setF((s) => ({
        ...s,
        lab_rate: String(labRate.loading_rate ?? ""),
        bag_lab_rate: String(labRate.replace_fill_rate ?? ""),
      }));
    } else if (labRate && !labRate.found) {
      setF((s) => ({ ...s, lab_rate: "", bag_lab_rate: "" }));
    }
  }, [labRate, f.lab_contractor_id, lotBagTypeId]);
  const labRateNum = Number(f.lab_rate || 0);
  const bagLabRateNum = Number(f.bag_lab_rate || 0);
  const labourWork = stockBags * labRateNum;
  const bagMaterialAmt = chargeBags * bagRate;
  const bagLabourAmt = chargeBags * bagLabRateNum;
  const dalaShort = Math.max(Number(f.expected_dala || 0) - Number(f.dala_from_vehicle || 0), 0);
  const expectedWt = lot && stockBags && lot.current_bags
    ? (Number(lot.current_weight) * stockBags) / Number(lot.current_bags)
    : 0;
  const moisture = Math.max(0, expectedWt - Number(f.net_weight || 0));
  const shrinkPct = expectedWt > 0 ? (moisture / expectedWt) * 100 : 0;
  const shrinkLimit = Number(lot?.item_shrinkage_pct ?? 1.5);
  const shrinkOver = moisture > 0 && shrinkPct > shrinkLimit + 1e-6;
  const canApproveShrink = ["Super Admin", "Management / Owner", "Warehouse Manager"].includes(user?.role);

  const submit = async (e) => {
    e.preventDefault();
    if (!online) return toast.error("You are offline. Stock transactions require a live connection.");
    if (!f.lot_id || !f.bags || !f.net_weight) return toast.error("Select a lot and enter bags & net weight.");
    if (lot && +f.bags > lot.current_bags) return toast.error(`Only ${lot.current_bags} bags available in this lot.`);
    if (f.lab_contractor_id && !f.lab_rate) {
      return toast.error("No loading rate in the contractor agreement for this bag type. Set it under Masters → Labour Rates.");
    }
    if (shrinkOver && !canApproveShrink) {
      return toast.error(`Weight variance ${shrinkPct.toFixed(2)}% exceeds permissible shrinkage ${shrinkLimit}%. Ask a manager to approve.`);
    }
    if (shrinkOver && canApproveShrink && !f.shrinkage_approved) {
      return toast.error("Tick ‘Approve excess shrinkage’ to post this outward.");
    }
    if (dalaShort > 0 && f.dala_recover_from === "SENDER" && !f.receiver_id) {
      return toast.error("Select a receiver to recover the dala shortfall from them.");
    }
    if ((tornQty || spillQty) && !lotBagTypeId) {
      return toast.error("Lot has no bag type — set bag type on the lot / inward so bag charges can be rated.");
    }
    if (chargeBags > 0 && f.bag_charge_to === "SENDER" && !f.receiver_id) {
      return toast.error("Select a receiver to charge torn/spillage bags to them.");
    }
    if (chargeBags > 0 && !f.lab_contractor_id) {
      return toast.error("Select a labour contractor — torn/spillage need bag labour (replace/fill) as well as bag material.");
    }
    if (chargeBags > 0 && !bagLabRateNum) {
      return toast.error("No Replace/Fill rate in the contractor agreement for this bag type. Set it under Masters → Labour Rates.");
    }
    const chargedTo = resolveChargeParty(f.bag_charge_to, f, lot);
    const contractorId = f.lab_contractor_id ? +f.lab_contractor_id : null;
    const body = {
      date: f.date, time: f.time, lot_id: +f.lot_id,
      receiver_id: f.receiver_id ? +f.receiver_id : null,
      vehicle_id: matchVehicle(m.vehicles, f.vehicle_id, f.vehicle_number)?.id || null, driver: f.driver,
      destination: f.destination, dispatch_reference: f.dispatch_reference,
      bags: stockBags, net_weight: +f.net_weight || 0, remarks: f.remarks,
      shrinkage_approved: shrinkOver && canApproveShrink && !!f.shrinkage_approved,
      labour: f.lab_contractor_id
        ? {
            contractor_id: contractorId, quantity: stockBags, rate: labRateNum,
            work_charged_to_id: lot?.stock_owner_id || null,
            expected_dala: +f.expected_dala || 0, dala_from_vehicle: +f.dala_from_vehicle || 0,
            dala_recovery_party_id: resolveChargeParty(f.dala_recover_from, f, lot),
            dala_recovery_from: f.dala_recover_from,
          }
        : null,
      bags_detail: [
        tornQty && {
          charge_type: "Torn", quantity: tornQty, rate: bagRate,
          labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
          charged_to_id: chargedTo, charge_to_role: f.bag_charge_to,
          reason: "Torn bags replaced on outward",
        },
        spillQty && {
          charge_type: "Spillage", quantity: spillQty, rate: bagRate,
          labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
          charged_to_id: chargedTo, charge_to_role: f.bag_charge_to,
          reason: "Spillage bags filled — added to dispatch, stock not reduced",
        },
      ].filter(Boolean),
    };
    setSubmitting(true);
    try {
      const { data } = await api.post("/txn/outward", body);
      toast.success(`Outward ${data.outward_number} posted successfully`);
      qc.invalidateQueries({ queryKey: ["reg-labour"] });
      qc.invalidateQueries();
      navigate("/registers");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (m.loading)
    return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>;

  return (
    <div data-testid="outward-page">
    <form onSubmit={submit}>
      <PageHeader
        title="Outward Entry"
        subtitle="Single-stage atomic dispatch — validated against available lot balance"
        action={
          <button data-testid="outward-submit" type="submit" disabled={submitting || !online}
            className="flex h-11 items-center gap-2 rounded-full bg-emerald-800 px-6 font-semibold text-white transition-colors hover:bg-emerald-900 active:scale-[0.99] disabled:opacity-60">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpFromLine size={18} />}
            Post Outward
          </button>
        }
      />

      {!online && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <WifiOff size={16} /> You are offline. Posting is disabled.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5">
            <SectionTitle>Select Lot</SectionTitle>
            <Field label="Lot" required>
              <SelectInput
                options={activeLots.map((l) => ({ value: l.id, label: `${l.lot_number} · ${l.stock_owner_name} · ${l.item_name} · ${fmtNum(l.current_bags)} bags` }))}
                value={f.lot_id} onChange={set("lot_id")} data-testid="outward-lot"
              />
            </Field>
            {lot && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Avail label="Available Bags" value={fmtNum(lot.current_bags)} />
                <Avail label="Available Weight" value={`${fmtNum(lot.current_weight)} Qtl`} />
                <Avail label="Location" value={`${lot.warehouse_name} / ${lot.stack_code}`} />
                <Avail label="Bag Type" value={lot.bag_type_name || "—"} />
              </div>
            )}
          </Card>

          <Card className="p-5">
            <SectionTitle>Dispatch Details</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Date" required><TextInput type="date" value={f.date} onChange={set("date")} /></Field>
              <Field label="Time"><TextInput type="time" value={f.time} onChange={set("time")} /></Field>
              <Field label="Receiver / Buyer"><SelectInput options={receiverOpts} value={f.receiver_id} onChange={set("receiver_id")} data-testid="outward-receiver" /></Field>
              <Field label="Vehicle">
                <SuggestInput
                  placeholder="Type vehicle number"
                  value={f.vehicle_number}
                  options={vehicleOptions(m.vehicles)}
                  onChange={(v) => setF((s) => ({ ...s, vehicle_number: v, vehicle_id: "" }))}
                  onSelect={(o) => setF((s) => ({ ...s, vehicle_id: o.value, vehicle_number: o.label, driver: o.raw?.driver || s.driver }))}
                />
              </Field>
              <Field label="Driver"><TextInput value={f.driver} onChange={set("driver")} /></Field>
              <Field label="Destination"><TextInput value={f.destination} onChange={set("destination")} /></Field>
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle>Quantity</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
              <Field label="Bags from stock" required><NumberInput value={f.bags} onChange={set("bags")} data-testid="outward-bags" /></Field>
              <Field label="Net Weight (Qtl)" required><NumberInput value={f.net_weight} onChange={set("net_weight")} data-testid="outward-net" /></Field>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <Field label="Torn bags">
                <NumberInput value={f.torn_qty} onChange={set("torn_qty")} data-testid="outward-torn-qty" />
              </Field>
              <Field label="Spillage bags">
                <NumberInput value={f.spill_qty} onChange={set("spill_qty")} data-testid="outward-spill-qty" />
              </Field>
            </div>
            <div className="mt-3 rounded-lg bg-stone-50 px-3 py-2 text-sm flex justify-between">
              <span className="text-stone-600">Dispatched bags</span>
              <span className="font-semibold">{dispatchedBags || 0}</span>
            </div>
            <p className="mt-1 text-xs text-stone-400">
              Stock is reduced by Bags from stock only. Spillage is added to dispatched bags but does not reduce lot stock.
            </p>
            {chargeBags > 0 && (
              <div className="mt-2 rounded-lg bg-stone-50 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-stone-600">Bag material ({chargeBags} × {fmtMoney(bagRate)})</span>
                  <span className="font-semibold">{fmtMoney(bagMaterialAmt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Bag labour replace/fill ({chargeBags} × {fmtMoney(bagLabRateNum)})</span>
                  <span className="font-semibold">{fmtMoney(bagLabourAmt)}</span>
                </div>
                <div className="flex justify-between border-t border-stone-200 pt-1">
                  <span className="text-stone-600">Total torn/spill charges</span>
                  <span className="font-semibold">{fmtMoney(bagMaterialAmt + bagLabourAmt)}</span>
                </div>
              </div>
            )}
            <div className="mt-3">
              <Field label="Bag charges to">
                <SelectInput options={CHARGE_WHO} value={f.bag_charge_to} onChange={set("bag_charge_to")} placeholder="" />
              </Field>
            </div>
            {lot && stockBags > 0 && (
              <div className={`mt-3 rounded-lg p-3 text-sm space-y-1 ${shrinkOver ? "bg-amber-50" : "bg-stone-50"}`}>
                <div className="flex justify-between"><span className="text-stone-600">Expected weight (lot average)</span><span>{fmtNum(expectedWt)} Qtl</span></div>
                <div className="flex justify-between"><span className="text-stone-600">Moisture / shrinkage</span><span>{fmtNum(moisture)} Qtl · {fmtNum(shrinkPct)}%</span></div>
                <div className="text-xs text-stone-500">Permissible {shrinkLimit}% (rent contract → item master → 1.5%). Bag count is not changed.</div>
                {shrinkOver && (
                  <p className="text-xs text-amber-800">Variance exceeds the limit. A Warehouse Manager or Admin must approve before posting.</p>
                )}
                {shrinkOver && canApproveShrink && (
                  <label className="mt-1 flex items-center gap-2 text-sm text-stone-800">
                    <input type="checkbox" checked={!!f.shrinkage_approved} onChange={(e) => setF((s) => ({ ...s, shrinkage_approved: e.target.checked }))} />
                    Approve excess shrinkage
                  </label>
                )}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-5">
            <SectionTitle>Loading Labour & Dala</SectionTitle>
            <div className="space-y-3">
              <Field label="Contractor"><SelectInput options={labourOpts} value={f.lab_contractor_id} onChange={set("lab_contractor_id")} /></Field>
              {f.lab_contractor_id && lotBagTypeId && labRate && !labRate.found && (
                <p className="text-xs text-amber-700">No loading rate for this contractor and bag type. Set it under Masters → Labour Rates.</p>
              )}
              {f.lab_contractor_id && !lotBagTypeId && (
                <p className="text-xs text-stone-400">Select a lot with a bag type to load the agreement rate.</p>
              )}
              <div className="rounded-lg bg-emerald-50 p-3 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Work payment</span><span className="font-semibold">{fmtMoney(labourWork)}</span></div>
                <div className="mt-1 text-xs text-stone-500">{stockBags || 0} bags from stock × ₹{f.lab_rate || 0} (agreement) · charged to stock owner</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Expected Dala"><NumberInput value={f.expected_dala} onChange={set("expected_dala")} /></Field>
                <Field label="From Vehicle"><NumberInput value={f.dala_from_vehicle} onChange={set("dala_from_vehicle")} /></Field>
              </div>
              <div className="rounded-lg bg-amber-50 p-3 text-sm flex justify-between">
                <span className="text-stone-600">Shortfall (SRM pays labour)</span><span className="font-semibold text-amber-800">{fmtMoney(dalaShort)}</span>
              </div>
              <Field label="Recover Shortfall From">
                <SelectInput options={CHARGE_WHO} value={f.dala_recover_from} onChange={set("dala_recover_from")} placeholder="" />
              </Field>
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle>Remarks</SectionTitle>
            <TextArea rows={3} value={f.remarks} onChange={set("remarks")} />
          </Card>
        </div>
      </div>
    </form>
    <TxnRecent kind="outward" title="Recent Outwards" columns={outwardColumns} editFields={outwardFields} />
    </div>
  );
}

const Avail = ({ label, value }) => (
  <div className="rounded-lg bg-stone-50 p-3">
    <div className="text-[11px] text-stone-500">{label}</div>
    <div className="font-semibold text-stone-900 text-sm">{value}</div>
  </div>
);
